import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import { TAG_SETS } from "@/components/ui/tags/CommonTagChips";

/**
 * Metadata Fields
 * Tags, notes, and internal flags.
 *
 * No `savedViewMembership` — these fields are not surfaced in default
 * saved views (see `../saved-views/ticket-saved-views.ts`).
 */
export const metadataFields = defineFieldConfigMap({
  // FIELD: tags
  tags: {
    type: "tags",
    label: "Tags",
    placeholder: "Add custom tags...",
    tagsConfig: {
      commonTags: TAG_SETS.TICKET,
      allowCustom: true,
      maxTags: 10,
    },
    // Table properties
    sortable: false,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "multiSelect",
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    icon: "Tag",
    // Tags can carry internal routing/automation labels — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "notes",
    graphqlSelector: "tags",
  },

  // FIELD: internal_notes
  internal_notes: {
    type: "textarea",
    label: "Internal Notes",
    placeholder: "Internal notes (not visible to customer)...",
    // Table properties
    sortable: false,
    filterable: false,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    icon: "FileText",
    // Internal — never expose to client_user.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "notes",
    graphqlSelector: "internal_notes",
  },

  // FIELD: customer_visible_notes
  customer_visible_notes: {
    type: "textarea",
    label: "Customer Notes",
    placeholder: "Notes visible to customer...",
    // Table properties
    sortable: false,
    filterable: false,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    icon: "FileText",
    mobileDetailSectionId: "notes",
    graphqlSelector: "customer_visible_notes",
  },

  // FIELD: is_internal
  is_internal: {
    type: "boolean",
    label: "Internal Ticket",
    placeholder: "Mark as internal ticket",
    // Table properties
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "exact",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    icon: "Settings",
    // Internal flag — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "notes",
    graphqlSelector: "is_internal",
  },

  // FIELD: has_time_entries
  has_time_entries: {
    type: "boolean",
    label: "Has Time Entries",
    placeholder: "Tickets with time entries",
    // Table properties
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false, // Auto-maintained by trigger
    filterType: "exact",
    width: 140,
    minWidth: 120,
    maxWidth: 180,
    icon: "Clock",
    // Internal ops signal — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "notes",
    graphqlSelector: "has_time_entries",
  },

  // FIELD: auto_assign
  auto_assign: {
    type: "boolean",
    label: "Auto Assign",
    placeholder: "Enable auto assignment",
    // Table properties
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "exact",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    icon: "Settings",
    // Internal automation — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "notes",
    graphqlSelector: "auto_assign",
  },

  // FIELD: auto_escalate
  auto_escalate: {
    type: "boolean",
    label: "Auto Escalate",
    placeholder: "Enable auto escalation",
    // Table properties
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "exact",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    icon: "Settings",
    // Internal automation — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "notes",
    graphqlSelector: "auto_escalate",
  },
});
